/**
 * dedupeUploads — partition an incoming batch of files against the
 * library's live document set and the current in-flight upload slots.
 *
 * Pure, DOM- and network-free so the shell handler stays thin and the
 * duplicate logic is unit-testable in isolation.
 *
 * Three-way split (case-insensitive on the file name):
 *   - blocked — name already exists as a document in the library. These
 *     are surfaced to the writer in a popup and NOT uploaded.
 *   - skipped — name duplicates an earlier file in the SAME batch (a
 *     flattened folder can carry the same name in two subfolders) or a
 *     file that is still uploading from an earlier batch. Silently
 *     dropped: expected folder clutter, not an error worth a popup.
 *   - allowed — everything else; uploaded as normal.
 *
 * Precedence per file: library collision (blocked) wins over an
 * in-batch / in-flight duplicate (skipped).
 */

export interface PartitionUploadsOptions {
  /** Names of documents that already live in the library (live fetch). */
  libraryNames: string[];
  /** Names of files still uploading from earlier batches (in-flight slots). */
  pendingNames: string[];
}

export interface PartitionUploadsResult {
  allowed: File[];
  blocked: File[];
  skipped: File[];
}

function normalize(name: string): string {
  return name.trim().toLowerCase();
}

export function partitionUploads(files: File[], opts: PartitionUploadsOptions): PartitionUploadsResult {
  const libraryNames = new Set(opts.libraryNames.map(normalize));
  const pendingNames = new Set(opts.pendingNames.map(normalize));
  const seenInBatch = new Set<string>();

  const allowed: File[] = [];
  const blocked: File[] = [];
  const skipped: File[] = [];

  for (const file of files) {
    const key = normalize(file.name);
    if (libraryNames.has(key)) {
      blocked.push(file);
      continue;
    }
    if (seenInBatch.has(key) || pendingNames.has(key)) {
      skipped.push(file);
      continue;
    }
    seenInBatch.add(key);
    allowed.push(file);
  }

  return { allowed, blocked, skipped };
}
